import { useMemo, useState } from 'react';

import { Box } from '@mui/material';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { CampaignChannels } from 'domains/campaigns/types';
import DirectMailPreviewContent from 'domains/content/components/DirectMailPreviewContent/DirectMailPreviewContent';
import EmailPreviewContent from 'domains/content/components/EmailPreviewContent/EmailPreviewContent';
import VoicePreviewContent from 'domains/content/components/VoicePreviewContent';
import { isTemplate, Template, DirectMailSenderProfile, SenderProfile, WhatsApp } from 'domains/content/types';
import LanguageTabBar from 'domains/core/components/LanguageTabBar';
import useContactColumns from 'hooks/queries/useContactColumns';
import { DEFAULT_LANGUAGE_CODE } from 'models/languages';
import SmsPreview from 'pages/ContentSmsBuilderPage/components/SmsPreview';
import { getFormattedSmsAutoResponses } from 'pages/ContentSmsBuilderPage/components/SmsPreview/utils';
import useFormatSmsContent from 'pages/ContentSmsBuilderPage/hooks/useFormatSmsContent';
import WhatsAppPreview from 'pages/ContentWhatsAppBuilderPage/components/WhatsAppPreview';
import useFormatWhatsAppContent from 'pages/ContentWhatsAppBuilderPage/hooks/useFormatWhatsAppContent';
import {
    getBodyComponentText,
    transformComponentsWithMergeTags,
} from 'pages/ContentWhatsAppBuilderPage/utils/whatsAppUtils';
import { getMergeTags } from 'utils';

const outerBoxStyles = {
    width: '100%',
    flex: 1, // Take all remaining space
    minHeight: 0, // Allow shrinking below content size
    backgroundColor: 'grey.200',
    borderRadius: '4px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden', // Hide overflow - inner content will handle scrolling
    margin: '32px 0',
    flexShrink: 0, // Prevent shrinking
};

const getInnerBoxStyles = (channel: CampaignChannels) => {
    const baseStyles = {
        width: '52%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '96%', // Always fill the outer box height
    };

    // For Email, Voice, and Direct Mail, expand to available height
    if (
        channel === CampaignChannels.EMAIL ||
        channel === CampaignChannels.VOICE_CALL ||
        channel === CampaignChannels.DIRECT_MAIL
    ) {
        return {
            ...baseStyles,
            overflow: 'hidden', // Hide overflow - preview components handle their own scrolling
        };
    }

    // For SMS and WhatsApp, center it (natural size)
    return {
        ...baseStyles,
        alignItems: 'center', // Center vertically
        justifyContent: 'center', // Center horizontally
    };
};

type Props = {
    channel: CampaignChannels;
    previewTemplate?: Template | object;
    emailSenderProfile?: SenderProfile;
    smsData?: { content?: string; autoResponses?: any[]; name?: string; smsMlJson?: Record<string, any> };
    mergeTags?: any;
    voiceData?: { voiceJson?: any; name?: string };
    previewDirectMail?: { html?: string; name?: string; htmlJson?: Record<string, string> };
    directMailSenderProfile?: DirectMailSenderProfile;
    whatsAppData?: WhatsApp;
};

const ContentPreviewScreen = ({
    channel,
    previewTemplate,
    emailSenderProfile,
    smsData,
    mergeTags,
    voiceData,
    previewDirectMail,
    directMailSenderProfile,
    whatsAppData,
}: Props) => {
    const { mlTemplateBuilder: isMlEnabled, newWhatsappBuilder: hasNewBuilder } = useFlags();

    // Language selection for multilingual WhatsApp preview
    const [selectedLanguage, setSelectedLanguage] = useState(DEFAULT_LANGUAGE_CODE);

    // Fetch contact columns for WhatsApp merge tags preview (similar to SMS)
    const { data: contactColumns, isSuccess: isContactColumnsSuccess } = useContactColumns({
        enabled: channel === CampaignChannels.WHATSAPP,
    });

    // Create merge tags from contact columns for WhatsApp preview
    const whatsAppMergeTags = useMemo(() => {
        if (isContactColumnsSuccess && contactColumns) {
            return getMergeTags(contactColumns);
        }
        return [];
    }, [contactColumns, isContactColumnsSuccess]);

    // Email multilingual — reads from htmlJson (per-language rendered HTML), subjectLineJson, preHeaderJson
    const emailLanguages = useMemo(() => {
        if (isMlEnabled && isTemplate(previewTemplate) && previewTemplate.htmlJson) {
            return Object.keys(previewTemplate.htmlJson);
        }
        return [DEFAULT_LANGUAGE_CODE];
    }, [isMlEnabled, previewTemplate]);

    const activeEmailHtml = useMemo(() => {
        if (isMlEnabled && isTemplate(previewTemplate) && previewTemplate.htmlJson?.[selectedLanguage]) {
            return previewTemplate.htmlJson[selectedLanguage];
        }
        return isTemplate(previewTemplate) ? previewTemplate.html : undefined;
    }, [isMlEnabled, previewTemplate, selectedLanguage]);

    const activeEmailSubjectLine = useMemo(() => {
        if (isMlEnabled && isTemplate(previewTemplate) && previewTemplate.subjectLineJson?.[selectedLanguage]) {
            return previewTemplate.subjectLineJson[selectedLanguage];
        }
        return isTemplate(previewTemplate) ? previewTemplate.subjectLine : undefined;
    }, [isMlEnabled, previewTemplate, selectedLanguage]);

    const activeEmailPreHeader = useMemo(() => {
        if (isMlEnabled && isTemplate(previewTemplate) && previewTemplate.preHeaderJson?.[selectedLanguage]) {
            return previewTemplate.preHeaderJson[selectedLanguage];
        }
        return isTemplate(previewTemplate) ? previewTemplate.preHeader : undefined;
    }, [isMlEnabled, previewTemplate, selectedLanguage]);

    // Languages available in the template (only when multilingual flag is on)
    const whatsAppLanguages = useMemo(() => {
        if (isMlEnabled && whatsAppData?.componentsJson) {
            return Object.keys(whatsAppData.componentsJson);
        }
        return [DEFAULT_LANGUAGE_CODE];
    }, [isMlEnabled, whatsAppData]);

    // SMS multilingual — reads from smsMlJson, gated by the same flag
    const smsLanguages = useMemo(() => {
        if (isMlEnabled && smsData?.smsMlJson) {
            return Object.keys(smsData.smsMlJson);
        }
        return [DEFAULT_LANGUAGE_CODE];
    }, [isMlEnabled, smsData]);

    const activeSmsContent = useMemo(() => {
        if (isMlEnabled && smsData?.smsMlJson?.[selectedLanguage]) {
            return smsData.smsMlJson[selectedLanguage]?.content?.text ?? '';
        }
        return smsData?.content;
    }, [isMlEnabled, smsData, selectedLanguage]);

    const activeSmsAutoResponses = useMemo(() => {
        if (isMlEnabled && smsData?.smsMlJson?.[selectedLanguage]) {
            return smsData.smsMlJson[selectedLanguage]?.autoResponses;
        }
        return smsData?.autoResponses;
    }, [isMlEnabled, smsData, selectedLanguage]);

    // Direct Mail multilingual — reads from htmlJson, gated by the same flag
    const directMailLanguages = useMemo(() => {
        if (isMlEnabled && previewDirectMail?.htmlJson) {
            return Object.keys(previewDirectMail.htmlJson);
        }
        return [DEFAULT_LANGUAGE_CODE];
    }, [isMlEnabled, previewDirectMail]);

    const activeDirectMailHtml = useMemo(() => {
        if (isMlEnabled && previewDirectMail?.htmlJson?.[selectedLanguage]) {
            return previewDirectMail.htmlJson[selectedLanguage];
        }
        return previewDirectMail?.html;
    }, [isMlEnabled, previewDirectMail, selectedLanguage]);

    // Full components array for the selected language — used by new builder preview
    // componentsJson stores raw {{field_name}} so no numeric transform needed
    // Single-language fallback: transform {{1}} → {{field_name}} from the backward-compat column
    const activeWhatsAppComponents = useMemo(() => {
        if (!whatsAppData) return undefined;
        if (isMlEnabled && whatsAppData.componentsJson) {
            return whatsAppData.componentsJson[selectedLanguage] ?? whatsAppData.components ?? [];
        }
        return transformComponentsWithMergeTags(whatsAppData.components ?? [], whatsAppData.templateFields ?? {});
    }, [whatsAppData, isMlEnabled, selectedLanguage]);

    // Body text for legacy preview — derived from the selected language's components
    const whatsAppContent = useMemo(() => {
        if (!whatsAppData) return '';
        if (isMlEnabled && whatsAppData.componentsJson) {
            const langComponents = whatsAppData.componentsJson[selectedLanguage];
            if (langComponents) return getBodyComponentText({ components: langComponents });
        }
        return getBodyComponentText(whatsAppData, whatsAppData.templateFields);
    }, [whatsAppData, isMlEnabled, selectedLanguage]);

    const formattedSmsPreviewContent = useFormatSmsContent(activeSmsContent, mergeTags);
    const formattedSmsAutoResponses = getFormattedSmsAutoResponses(activeSmsAutoResponses, mergeTags);
    // Use whatsAppMergeTags to show preview values (e.g., "Taylor" instead of "{{first_name}}")
    const formattedWhatsAppContent = useFormatWhatsAppContent(whatsAppContent, whatsAppMergeTags);

    const showLanguageTabs =
        (channel === CampaignChannels.EMAIL && isMlEnabled && emailLanguages.length > 1) ||
        (channel === CampaignChannels.WHATSAPP && isMlEnabled && whatsAppLanguages.length > 1) ||
        (channel === CampaignChannels.SMS && isMlEnabled && smsLanguages.length > 1) ||
        (channel === CampaignChannels.DIRECT_MAIL && isMlEnabled && directMailLanguages.length > 1);

    const activeLanguages =
        channel === CampaignChannels.EMAIL
            ? emailLanguages
            : channel === CampaignChannels.SMS
            ? smsLanguages
            : channel === CampaignChannels.DIRECT_MAIL
            ? directMailLanguages
            : whatsAppLanguages;

    // Render preview based on channel using switch
    const renderPreview = () => {
        switch (channel) {
            case CampaignChannels.EMAIL:
                return (
                    <EmailPreviewContent
                        emailTemplateHTML={activeEmailHtml}
                        fromAddress={emailSenderProfile?.from_address}
                        subjectLine={activeEmailSubjectLine}
                        preHeader={activeEmailPreHeader}
                        isGridItem
                        isOpen={true}
                        isCloseEnabled={false}
                        onClose={() => {}}
                        previewLabel=""
                    />
                );

            case CampaignChannels.SMS:
                return (
                    <SmsPreview
                        formattedContent={formattedSmsPreviewContent}
                        autoResponses={formattedSmsAutoResponses}
                    />
                );

            case CampaignChannels.VOICE_CALL:
                return (
                    <VoicePreviewContent
                        isOpen={true}
                        isCloseEnabled={false}
                        onClose={() => {}}
                        voiceJson={voiceData?.voiceJson}
                        previewLabel=""
                    />
                );

            case CampaignChannels.DIRECT_MAIL:
                return (
                    <DirectMailPreviewContent
                        isOpen={true}
                        htmlContent={activeDirectMailHtml}
                        isGridItem
                        isCloseEnabled={false}
                        onClose={() => {}}
                        directMailSender={directMailSenderProfile}
                        previewLabel=""
                    />
                );

            case CampaignChannels.WHATSAPP:
                // New builder: pass full components array so header/footer/buttons render
                // Legacy builder: pass formatted body text string only
                return hasNewBuilder ? (
                    <WhatsAppPreview components={activeWhatsAppComponents} mergeTags={whatsAppMergeTags} />
                ) : (
                    <WhatsAppPreview formattedContent={formattedWhatsAppContent} />
                );
        }
    };

    return (
        <>
            {showLanguageTabs && (
                <LanguageTabBar
                    languages={activeLanguages}
                    selectedLanguage={selectedLanguage}
                    onSelectLanguage={setSelectedLanguage}
                    onAddLanguage={() => {}}
                />
            )}
            <Box sx={outerBoxStyles}>
                <Box sx={getInnerBoxStyles(channel)}>{renderPreview()}</Box>
            </Box>
        </>
    );
};

export default ContentPreviewScreen;
